import React, { useEffect, useMemo, useState } from "react";
import api, { fileUrl } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import BulkCsvDialog from "@/components/BulkCsvDialog";

const EMPTY = { name: "", tournament_ids: [], logo_url: "", short_name: "", color: "#FFFFFF", category_by_tournament: {} };

const COLOR_PRESETS = [
  "#FFFFFF", "#FFD60A", "#0A84FF", "#FF3B30", "#30D158",
  "#5E5CE6", "#FF9F0A", "#000000", "#8E8E93", "#BF5AF2",
];

export default function AdminTeams() {
  const [items, setItems] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [filter, setFilter] = useState("all");
  const [bulkOpen, setBulkOpen] = useState(false);

  const load = async () => {
    const [t, tt] = await Promise.all([api.get("/teams"), api.get("/tournaments")]);
    setItems(t.data); setTournaments(tt.data);
  };
  useEffect(() => { load(); }, []);

  const tournamentById = useMemo(() => Object.fromEntries(tournaments.map((t) => [t.id, t])), [tournaments]);

  const startCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, tournament_ids: tournaments[0] ? [tournaments[0].id] : [], category_by_tournament: {} });
    setOpen(true);
  };
  const startEdit = (t) => {
    setEditing(t);
    const tids = (t.tournament_ids && t.tournament_ids.length ? t.tournament_ids : (t.tournament_id ? [t.tournament_id] : []));
    // Prefer explicit per-tournament map; fallback to legacy category_id for primary tournament
    const cbt = { ...(t.category_by_tournament || {}) };
    if (t.category_id && tids[0] && !cbt[tids[0]]) cbt[tids[0]] = t.category_id;
    setForm({ ...EMPTY, ...t, tournament_ids: tids, category_by_tournament: cbt });
    setOpen(true);
  };

  const toggleTournament = (id) => {
    setForm((f) => {
      const has = (f.tournament_ids || []).includes(id);
      const next = has ? f.tournament_ids.filter((x) => x !== id) : [...(f.tournament_ids || []), id];
      // Clean up per-tournament category entries for tournaments no longer selected
      const nextCat = { ...(f.category_by_tournament || {}) };
      Object.keys(nextCat).forEach((tid) => { if (!next.includes(tid)) delete nextCat[tid]; });
      return { ...f, tournament_ids: next, category_by_tournament: nextCat };
    });
  };

  const setCategoryForTournament = (tid, cid) => {
    setForm((f) => ({ ...f, category_by_tournament: { ...(f.category_by_tournament || {}), [tid]: cid } }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.tournament_ids || form.tournament_ids.length === 0) return toast.error("Selecione ao menos um torneio");
    // For each selected tournament that HAS categories, require a category selection
    for (const tid of form.tournament_ids) {
      const tt = tournamentById[tid];
      const cats = tt?.categories || [];
      if (cats.length > 0 && !form.category_by_tournament?.[tid]) {
        return toast.error(`Selecione a categoria para o torneio "${tt?.name || tid}"`);
      }
    }
    try {
      const payload = {
        ...form,
        tournament_id: form.tournament_ids[0],
        category_id: form.category_by_tournament?.[form.tournament_ids[0]] || null,
        category_by_tournament: form.category_by_tournament || {},
      };
      if (editing) await api.put(`/teams/${editing.id}`, payload);
      else await api.post("/teams", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete team "${t.name}" and all its players?`)) return;
    await api.delete(`/teams/${t.id}`); toast.success("Deleted"); load();
  };

  const filtered = filter === "all" ? items : items.filter((x) => (x.tournament_ids || [x.tournament_id]).includes(filter));
  const tnameMap = Object.fromEntries(tournaments.map((t) => [t.id, t.name]));

  return (
    <div className="p-6 md:p-10">
      <PageHeader
        title="Teams"
        subtitle="Cadastre times em um ou mais torneios. Máx. 10 jogadores por time."
        testid="page-teams"
        action={
          <div className="flex flex-wrap gap-2">
            <Button
              data-testid="bulk-import-teams-btn"
              onClick={() => setBulkOpen(true)}
              disabled={tournaments.length === 0}
              variant="outline"
              className="bg-transparent border-navy-border text-white hover:bg-navy-surface hover:text-orange font-heading uppercase tracking-widest rounded-sm"
            >
              <Upload size={16} className="mr-2" /> Importar CSV
            </Button>
            <Button data-testid="create-team-btn" onClick={startCreate} disabled={tournaments.length === 0} className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
              <Plus size={16} className="mr-2" /> New team
            </Button>
          </div>
        }
      />

      {tournaments.length === 0 && (
        <div className="border border-orange/40 bg-orange/5 text-orange p-4 rounded-sm mb-4 text-sm">
          Create a tournament first.
        </div>
      )}

      <div className="mb-4 max-w-xs">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="teams-filter" className="bg-navy-deep border-navy-border text-white rounded-sm">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-navy-deep border-navy-border text-white">
            <SelectItem value="all">All tournaments</SelectItem>
            {tournaments.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((t) => (
          <div key={t.id} className="bg-navy-deep border border-navy-border rounded-sm p-4 flex items-center gap-3" data-testid={`team-row-${t.id}`}>
            <div className="w-1.5 h-14 rounded-sm shrink-0" style={{ background: t.color || "#FFFFFF" }} />
            <div className="w-14 h-14 rounded-sm bg-navy-surface border border-navy-border grid place-items-center overflow-hidden shrink-0">
              {t.logo_url ? <img src={fileUrl(t.logo_url)} alt="" className="w-full h-full object-cover" />
                : <span className="font-heading font-black text-2xl text-orange">{t.name?.[0]}</span>}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-bold uppercase tracking-tight text-white truncate">{t.name}</div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted truncate">
                {((t.tournament_ids && t.tournament_ids.length ? t.tournament_ids : [t.tournament_id]).map((id) => tnameMap[id]).filter(Boolean).join(" · ")) || "—"}
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <Button data-testid={`edit-team-${t.id}`} size="icon" variant="ghost" onClick={() => startEdit(t)} className="text-ink-secondary hover:text-orange hover:bg-navy-surface"><Pencil size={14} /></Button>
              <Button data-testid={`delete-team-${t.id}`} size="icon" variant="ghost" onClick={() => remove(t)} className="text-ink-secondary hover:text-court-red hover:bg-navy-surface"><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && tournaments.length > 0 && (
          <div className="col-span-full text-center text-ink-muted p-8">No teams yet.</div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-navy-deep border-navy-border text-white max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight text-2xl">{editing ? "Edit team" : "New team"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="flex flex-col flex-1 min-h-0" data-testid="team-form">
            <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-4">
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Team name</Label>
              <Input data-testid="team-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-navy border-navy-border mt-2 rounded-sm" />
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label className="text-ink-secondary uppercase tracking-widest text-xs">Torneios (selecione um ou mais)</Label>
                <div data-testid="team-tournaments-multi" className="mt-2 border border-navy-border rounded-sm bg-navy p-2 max-h-72 overflow-y-auto space-y-1">
                  {tournaments.length === 0 && <div className="text-xs text-ink-muted p-2">Nenhum torneio cadastrado</div>}
                  {tournaments.map((t) => {
                    const checked = (form.tournament_ids || []).includes(t.id);
                    return (
                      <label
                        key={t.id}
                        data-testid={`team-tournament-toggle-${t.id}`}
                        className={`flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer transition-colors ${checked ? "bg-orange/10 border border-orange/40" : "border border-transparent hover:bg-navy-surface"}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleTournament(t.id)}
                          className="accent-orange"
                        />
                        <span className="text-sm text-white font-heading uppercase tracking-tight">{t.name}</span>
                        {(t.categories && t.categories.length > 0) && <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-ink-muted">{t.categories.length} cat.</span>}
                      </label>
                    );
                  })}
                </div>
              </div>
              {(() => {
                // Per-tournament category pickers — one per selected tournament that HAS categories
                const withCats = (form.tournament_ids || [])
                  .map((tid) => tournamentById[tid])
                  .filter((tt) => tt && (tt.categories || []).length > 0);
                return (
                  <div>
                    <Label className="text-ink-secondary uppercase tracking-widest text-xs">Categorias por torneio</Label>
                    <div className="mt-2 border border-navy-border rounded-sm bg-navy p-2 max-h-72 overflow-y-auto space-y-3">
                      {withCats.length === 0 ? (
                        <div className="text-xs text-ink-muted p-2">Selecione torneios que possuam categorias para atribuir aqui</div>
                      ) : withCats.map((tt) => (
                        <div key={tt.id} data-testid={`team-category-block-${tt.id}`}>
                          <div className="text-[10px] font-mono uppercase tracking-widest text-orange mb-1">{tt.name}</div>
                          <Select
                            value={form.category_by_tournament?.[tt.id] || ""}
                            onValueChange={(v) => setCategoryForTournament(tt.id, v)}
                          >
                            <SelectTrigger data-testid={`team-category-select-${tt.id}`} className="bg-navy border-navy-border text-white rounded-sm">
                              <SelectValue placeholder="Selecione a categoria…" />
                            </SelectTrigger>
                            <SelectContent className="bg-navy-deep border-navy-border text-white">
                              {(tt.categories || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
            <ImageUpload label="Team logo" value={form.logo_url} onChange={(url) => setForm({ ...form, logo_url: url })} testid="team-logo" />
            <div>
              <Label className="text-ink-secondary uppercase tracking-widest text-xs">Team color (used in live operator panel)</Label>
              <div className="mt-2 flex items-center gap-3 flex-wrap">
                <div
                  className="w-12 h-10 rounded-sm border border-navy-border shrink-0"
                  style={{ background: form.color || "#FFFFFF" }}
                  data-testid="team-color-preview"
                />
                <Input
                  data-testid="team-color-input"
                  type="text"
                  value={form.color || ""}
                  onChange={(e) => setForm({ ...form, color: e.target.value })}
                  placeholder="#FFFFFF"
                  className="bg-navy border-navy-border mt-0 rounded-sm font-mono w-32"
                />
                <div className="flex flex-wrap gap-1">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      data-testid={`team-color-preset-${c.replace('#', '').toLowerCase()}`}
                      onClick={() => setForm({ ...form, color: c })}
                      className={`w-7 h-7 rounded-sm border-2 transition-all ${form.color === c ? "border-orange scale-110" : "border-navy-border"}`}
                      style={{ background: c }}
                      title={c}
                    />
                  ))}
                </div>
              </div>
            </div>
            </div>
            <DialogFooter className="pt-4 border-t border-navy-border mt-4 shrink-0">
              <Button data-testid="team-submit-btn" type="submit" className="bg-orange hover:bg-orange-hover text-white font-heading uppercase tracking-widest rounded-sm">
                {editing ? "Save changes" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkCsvDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        endpoint="/teams/bulk-csv"
        title="Importar times (CSV)"
        templateHeader="name,color,tournaments,categories"
        templateExample={`Team Thunder,#FF3B30,${tournaments[0]?.name || "Copa Verão 2026"},${(tournaments[0]?.categories && tournaments[0].categories[0]?.name) || ""}\nTeam Lightning,#0A84FF,${tournaments[0]?.name || "Copa Verão 2026"}${tournaments[1]?.name ? `;${tournaments[1].name}` : ""},${(tournaments[0]?.categories && tournaments[0].categories[0]?.name) || ""}${tournaments[1]?.name ? `;${(tournaments[1].categories && tournaments[1].categories[0]?.name) || ""}` : ""}`}
        templateFilename="teams_template.csv"
        columnHelp={[
          { name: "name", desc: "(obrigatório) Nome do time" },
          { name: "color", desc: "(opcional) Cor em hexadecimal (ex.: #FF3B30)" },
          { name: "tournaments (ou tournament / tournament_id)", desc: "(obrigatório) Um OU mais torneios (nomes ou IDs) separados por ; ou |" },
          { name: "categories (ou category / category_id)", desc: "(opcional) Categoria POR torneio, na MESMA ordem, separadas por ; ou |. Ex.: Sub-19;Adulto" },
        ]}
        onDone={load}
      />
    </div>
  );
}
